import React from "react";
import { useLocation } from "react-router-dom";
import { PageHeader } from "components/ui";
import useApiQuery, { Table } from "hooks/useQuery/api-query";
import useRowSelect, { SelectRowContext } from "hooks/useRowSelectNew";
import { useOrdersTable, OrdersTableSettingsContext } from "./use-table";
import OrdersStatusTableControls from "./table-controls";
import OrderTableRow from "./table-row";

const OrderStatusTable = () => {
  const location = useLocation();

  const orderStatus = location?.state?.status ?? "new";
  const statusText = displayOrderStatusText(orderStatus);
  const initialQuery = {
    sort: { by: "submitted", direction: "desc" },
    filters: { 
      status: orderStatus, 
      order: "all",
      product_line: ["CPAP"]
    },
    pagination: { page: 1, per: 150 }
  };
  const { visibleColumnFields, tableColumns, tableSettings, updateTableSettings, resetTableSettings } = useOrdersTable(orderStatus);
  const { query, queryDispatcher, rows, data, isFetching, fetchData } =
    useApiQuery("orders", initialQuery, "orders");
  const {
    toggleAllRows,
    isRowSelected,
    toggleRow,
    clearSelectedRows,
    selectedRows
  } = useRowSelect(rows, "id", query, row => ({
    invoice_number: row.invoice_number
  }));
  React.useEffect(() => {
    queryDispatcher({
      type: "updateQuery",
      ...initialQuery
    });
  }, [orderStatus]);
  return (
    <div key={`${orderStatus}`} className="order-status-table">
      <PageHeader title={`${statusText} Orders`} />
      <OrdersTableSettingsContext.Provider
        value={{
          visibleColumnFields,
          tableColumns,
          resetTableSettings,
          tableSettings,
          updateTableSettings
        }}
      >
      <SelectRowContext.Provider
        value={{
          toggleAllRows,
          toggleRow,
          selectedRows,
          isRowSelected,
          clearSelectedRows
        }}
      >
        <Table
          columns={tableColumns.filter(tableColumn => {
            if (Object.prototype.hasOwnProperty.call(tableSettings, tableColumn.field)) {
              return tableSettings[tableColumn.field].active || false;
            }
            else {
              return visibleColumnFields.includes(tableColumn.field);
            }
          })}
          rows={rows}
          query={query}
          data={data}
          queryDispatcher={queryDispatcher}
          isFetching={isFetching}
          fetchData={fetchData}
          containerClass="query-table orders-table static-columns"
          TableRow={OrderTableRow}
          rowKey="id"
          perPageOptions={[
            { text: "50", value: 50 },
            { text: "150", value: 150 },
            { text: "500", value: 500 }
          ]}
          Controls={
            <OrdersStatusTableControls
              query={query}
              selectedRows={selectedRows}
              queryDispatcher={queryDispatcher}
              clearSelectedRows={clearSelectedRows}
              isFetching={isFetching}
              refreshData={() => {
                fetchData(query);
              }}
            />
          }
        />
      </SelectRowContext.Provider>
      </OrdersTableSettingsContext.Provider>
    </div>
  );
};

function displayOrderStatusText(status) {
  switch (status) {
    case "new":
      return "New";
    case "completed":
      return "Completed";
    case "delayed":
      return "Delayed";
    case "pending":
      return "Pending Confirmation";
    case "ready":
      return "Accepted";
    case "follow_up":
      return "Coach Follow Up";
    case "on_hold":
      return "On Hold";
    case "incorrect":
      return "Incorrect";
    case "billed":
      return "Billed";
    case "rejected":
      return "Rejected";
    case "pending_buildout":
      return "Pending Buildout";
    case "location_assistance_needed":
      return "Location Assistance Needed";
    case "integration_follow_up":
      return "Integration Follow Up";
    case "ready_for_review":
      return "Ready for Review";
    case "internal_processing":
      return "Internal Processing";
    case "voided":
      return "Voided";
    case "accepted":
      return "Accepted";
    case "backordered":
      return "Backordered";
    case "pending_fulfillment":
      return "Pending Fulfillment";
    case "pending_shipment":
      return "Pending Shipment";
    case "order_loading":
      return "Order Loading";
    default:
      return status;
  }
}

export default OrderStatusTable;
